import React from "react";

import { IconCopyOutlined } from "~/components/icon";
import { Truncated } from "~/components/truncated";
import { cx } from "~/tools/class-variance";
import { copyToClipboard } from "~/tools/copy-to-clipboard";

import { DONT_AUTO_DISMISS, notification } from "../notification/Notification";
import { Tooltip } from "../tooltip/Tooltip";

export interface TagProps {
	text: string;

	/**
	 * Enables `onClick` when `true`
	 *
	 * @default false
	 */
	active?: boolean;

	/**
	 * Highlights the Tag when `true && active=true`
	 *
	 * @default false
	 */
	selected?: boolean;

	/**
	 * Shows copy to clipboard button on hover when `true`
	 *
	 * @default true
	 */
	withCopyToClipboard?: boolean;

	/**
	 * Disabled when `active=false`
	 */
	onClick?(text: string): void;
}

export const Tag: React.FC<TagProps> = ({
	text,
	active = false,
	selected = false,
	withCopyToClipboard = true,
	onClick,
}) => {
	// active only when both active and onClick handler is defined
	const isActive = active && onClick != null;

	return (
		<div className="relative flex max-w-full">
			<Tooltip title={text}>
				{/* it should not contain padding as a style, otherwise it will have the wrong calculation for firefox */}
				<Truncated
					className={cx(
						"peer max-w-full rounded-full border border-solid px-3 font-normal text-xs/5.5 outline-hidden transition-colors",
						isActive
							? [
									"cursor-pointer bg-white hover:border-primary-600 hover:text-primary-600",
									selected
										? "border-primary-600 text-primary-600 hover:bg-primary-600 hover:text-white"
										: "border-gray-375 text-gray-800",
								]
							: "cursor-default border-gray-375 bg-gray-375 text-gray-800",
					)}
					data-testid="expandable-list-item"
					el="span"
					onClick={(event) => {
						event.stopPropagation();
						if (isActive) {
							onClick(text!);
						}
					}}
					truncate="middle"
				>
					{text}
				</Truncated>
			</Tooltip>

			{withCopyToClipboard && (
				<Tooltip title="Copy to clipboard">
					<button
						className="invisible absolute -top-0.5 right-0 flex size-[26px] cursor-pointer items-center justify-center rounded-full border border-gray-375 bg-white text-gray-800 hover:visible hover:text-primary-600 peer-hover:visible"
						onClick={(event) => {
							event.stopPropagation();

							const [failure, success] = copyToClipboard(text);

							if (failure == null) {
								notification.success({ message: success });
							} else {
								notification.error({
									message: failure,
									duration: DONT_AUTO_DISMISS,
								});
							}
						}}
					>
						<IconCopyOutlined />
					</button>
				</Tooltip>
			)}
		</div>
	);
};

Tag.displayName = "Tag";
